import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import Lenis from 'lenis';
import { Phone, Menu, X, ChevronRight, ArrowUpRight, ChevronsLeftRight } from 'lucide-react';
import { Button } from './components/ui/button';
import JackWashQuoteModal from './components/JackWashQuoteModal';
import AdminPage from './components/AdminPage';

const LOGO_URL = 'https://customer-assets-jt897jd0.emergentagent.net/job_premium-quote-book/artifacts/1jenmsmv_IMG-20260907-WA0094.jpg';
const HERO_IMAGE = 'https://images.unsplash.com/photo-1625137357200-b722956bde26?crop=entropy&cs=srgb&fm=jpg&q=85';
const GOOGLE_REVIEWS_URL = 'https://maps.app.goo.gl/Lavzz7szecTUWZ7C6?g_st=iw';

// Real Google reviews — transcribed verbatim. New reviews are added here.
const GOOGLE_REVIEWS = [];
const PHONE_DISPLAY = '+61 425 447 518';
const PHONE_TEL = 'tel:+61425447518';
const EMAIL = 'JackWash.aus@gmail.com';
const INSTAGRAM_URL = 'https://www.instagram.com/jackwash.aus';
const WHATSAPP_URL = 'https://wa.me/61425447518';
const WATER_NOTE = 'Please ensure water and electricity are available at the property for your appointment.';

// Real pairs first; remaining slots show a premium placeholder until photos arrive.
const SLIDERS = [
  {
    title: 'Family SUV — Rear Seat Restoration',
    before: { src: '/images/ba-seat-before.jpg', alt: 'Fabric rear seat before JackWash detailing' },
    after: { src: '/images/ba-seat-after.jpg', alt: 'Fabric rear seat after JackWash detailing' },
  },
  {
    title: 'Mercedes-AMG — Full Interior Revival',
    before: { src: '/images/ba-amg-before.jpg', alt: 'Messy AMG interior before JackWash detailing' },
    after: { src: '/images/ba-amg-after.jpg', alt: 'Pristine AMG interior after JackWash detailing' },
  },
  {
    title: 'Work Van — Driver Cabin Reset',
    before: { src: '/images/ba-entry-before.jpg', alt: 'Dirty van driver footwell before JackWash detailing' },
    after: { src: '/images/ba-entry-after.jpg', alt: 'Clean van driver footwell after JackWash detailing' },
  },
  { title: 'Transformation 04 — Photos Coming Soon', before: null, after: null },
  { title: 'Transformation 05 — Photos Coming Soon', before: null, after: null },
  { title: 'Transformation 06 — Photos Coming Soon', before: null, after: null },
];

const GALLERY_IMAGES = [
  { src: '/images/ba-amg-after.jpg', alt: 'Mercedes-AMG interior after JackWash detailing', label: 'Mercedes-AMG — Interior Revival' },
  { src: '/images/ba-entry-after.jpg', alt: 'Work van driver cabin after JackWash detailing', label: 'Work Van — Cabin Reset' },
  { src: '/images/ba-seat-after.jpg', alt: 'Family SUV rear seat after JackWash detailing', label: 'Family SUV — Seat Restoration' },
  { src: '/images/jackwash-owner-van.jpg', alt: 'Jack with the branded JackWash detailing van on the Gold Coast', label: 'The JackWash Rig — Gold Coast' },
];

const EASE = [0.22, 1, 0.36, 1];

const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.04 2a9.9 9.9 0 0 0-8.4 15.13L2 22l5.02-1.58A9.9 9.9 0 1 0 12.04 2zm5.8 14.12c-.24.68-1.4 1.3-1.95 1.35-.5.04-1.13.2-3.8-.8-3.2-1.26-5.24-4.53-5.4-4.74-.16-.21-1.3-1.73-1.3-3.3 0-1.57.82-2.34 1.11-2.66.29-.32.63-.4.84-.4h.6c.2 0 .46-.07.71.54.26.62.88 2.14.96 2.3.08.16.13.34.02.55-.1.21-.16.34-.31.53-.16.18-.33.42-.47.56-.16.16-.32.34-.14.66.18.32.8 1.33 1.73 2.15 1.19 1.06 2.19 1.39 2.5 1.55.31.16.5.13.68-.08.18-.21.79-.92 1-1.24.21-.32.42-.26.7-.16.29.1 1.84.87 2.15 1.03.32.16.53.24.6.37.08.13.08.76-.16 1.44z" />
  </svg>
);

const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M21.35 11.1h-9.17v2.73h5.51c-.5 2.34-2.45 3.43-5.51 3.43a6.06 6.06 0 1 1 0-12.12c1.54 0 2.94.55 4.03 1.46l2.11-2.11A7.63 7.63 0 1 0 12.18 19.7c3.82 0 7.35-2.76 7.35-7.64 0-.32-.06-.64-.18-.96z" />
  </svg>
);

const FadeUp = ({ children, delay = 0, className = '', ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

// Interactive before/after reveal slider — drag or swipe.
function BeforeAfterSlider({ pair }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100)));
  };

  const startDrag = (event) => {
    update(event.clientX);
    const move = (ev) => update(ev.clientX);
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={startDrag}
      role="slider"
      aria-label="Before and after comparison slider"
      aria-valuenow={Math.round(pos)}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') setPos((p) => Math.max(4, p - 5));
        if (event.key === 'ArrowRight') setPos((p) => Math.min(96, p + 5));
      }}
      className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden select-none touch-none cursor-ew-resize border border-neutral-800 bg-neutral-900"
      data-testid="before-after-slider"
    >
      <img src={pair.after.src} alt={pair.after.alt} draggable={false} className="absolute inset-0 h-full w-full object-cover" data-testid="slider-after-image" />
      <img
        src={pair.before.src}
        alt={pair.before.alt}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        data-testid="slider-before-image"
      />
      <span className="absolute top-4 left-4 bg-red-600 text-white text-[10px] font-heading font-bold uppercase tracking-widest px-3 py-1 pointer-events-none">Before</span>
      <span className="absolute top-4 right-4 bg-yellow-500 text-black text-[10px] font-heading font-bold uppercase tracking-widest px-3 py-1 pointer-events-none">After</span>
      <div className="absolute inset-y-0 pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-yellow-400" />
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-11 w-11 rounded-full bg-yellow-500 text-black flex items-center justify-center shadow-xl shadow-black/60">
          <ChevronsLeftRight className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

// Elegant placeholder for transformations awaiting photos.
function PlaceholderSlide({ title, testid }) {
  return (
    <div className="relative aspect-[4/3] sm:aspect-[16/10] border border-dashed border-neutral-700 bg-neutral-900/60 flex flex-col items-center justify-center gap-3 text-center px-6" data-testid={testid}>
      <ChevronsLeftRight className="w-6 h-6 text-yellow-500/60" />
      <p className="font-heading font-bold text-sm uppercase tracking-widest text-neutral-400">{title}</p>
      <p className="text-xs text-neutral-600">New JackWash transformation photos are on the way.</p>
    </div>
  );
}

export default function App() {
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAllTransforms, setShowAllTransforms] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ anchors: true, duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => { lenis.raf(time); rafId = requestAnimationFrame(raf); };
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  const openQuote = () => { setMobileMenuOpen(false); setIsQuoteOpen(true); };

  if (typeof window !== 'undefined' && window.location.pathname === '/admin') {
    return <AdminPage />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white selection:bg-red-600 selection:text-white overflow-x-hidden">
      {/* Header */}
      <header className="absolute top-0 z-50 w-full">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3" data-testid="brand-logo">
            <img src={LOGO_URL} alt="JackWash logo" className="h-11 w-11 rounded-full border border-red-500/50 object-cover shadow-lg shadow-red-950/50" data-testid="brand-logo-image" />
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <a href={PHONE_TEL} className="text-xs text-neutral-300 hover:text-white flex items-center gap-1" data-testid="header-phone-link">
              <Phone className="w-3.5 h-3.5 text-yellow-500" /> {PHONE_DISPLAY}
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="JackWash on Instagram" className="text-neutral-300 hover:text-yellow-500 transition-colors" data-testid="header-instagram-link">
              <InstagramIcon className="w-4 h-4" />
            </a>
            <Button
              onClick={openQuote}
              className="bg-red-600 hover:bg-red-700 text-white font-heading font-bold tracking-wider px-6 py-2.5 rounded-none border border-red-500/50 shadow-lg shadow-red-600/30 uppercase transition-transform hover:-translate-y-0.5"
              data-testid="header-get-quote-btn"
            >
              Get Your Quote
            </Button>
          </div>

          <button
            className="md:hidden text-white focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-neutral-950/95 backdrop-blur border-b border-neutral-800 px-6 py-6 space-y-4">
            <a href="#difference" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium text-neutral-200">See The Difference</a>
            <a href="#work" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium text-neutral-200">Our Work</a>
            <a href="#reviews" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium text-neutral-200">Reviews</a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block text-lg font-medium text-neutral-200">Contact</a>
            <div className="pt-4 border-t border-neutral-800 space-y-4">
              <a href={PHONE_TEL} onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 text-lg font-medium text-yellow-500" data-testid="mobile-phone-link">
                <Phone className="w-5 h-5" /> {PHONE_DISPLAY}
              </a>
              <Button
                onClick={openQuote}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-heading font-bold tracking-wider py-3 rounded-none uppercase"
                data-testid="mobile-get-quote-btn"
              >
                Get Your Quote
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Luxury car exterior detailed by JackWash"
            className="w-full h-full object-cover object-center"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/60" />
        </div>

        <div className="relative z-10 text-center px-6 space-y-8 max-w-4xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
            className="font-heading font-black text-6xl sm:text-8xl tracking-tight uppercase"
            data-testid="hero-headline"
          >
            JACK<span className="text-red-600">WASH</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
            className="text-lg sm:text-xl text-neutral-300 font-light tracking-wide"
            data-testid="hero-subheadline"
          >
            Premium Mobile Car Detailing — Gold Coast
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
          >
            <Button
              onClick={openQuote}
              className="bg-red-600 hover:bg-red-700 text-white font-heading font-black text-lg tracking-wider px-10 py-7 rounded-none border border-red-500 shadow-2xl shadow-red-600/40 uppercase transition-transform hover:scale-105 active:scale-95"
              data-testid="hero-cta-quote"
            >
              Get Your Quote &amp; Book Appointment <ChevronRight className="w-5 h-5 text-yellow-400" />
            </Button>
            <a
              href="#contact"
              className="border border-neutral-500 bg-black/40 hover:bg-neutral-900 hover:border-yellow-500/60 text-white font-heading font-bold text-lg tracking-wider px-10 py-4 rounded-none uppercase transition-colors"
              data-testid="hero-cta-contact"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* BEFORE & AFTER SLIDERS — expandable */}
      <section id="difference" className="py-20 px-6 bg-[#0D0D0D] border-t border-neutral-900" data-testid="difference-section">
        <div className="max-w-4xl mx-auto space-y-10">
          <FadeUp className="text-center space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">Drag The Handle</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight uppercase">SEE THE <span className="gold-gradient-text">DIFFERENCE</span></h2>
            <p className="text-neutral-400 text-sm max-w-md mx-auto">Swipe to compare real JackWash results — exactly how each vehicle arrived, and how it left.</p>
          </FadeUp>

          <div className="space-y-8">
            {(showAllTransforms ? SLIDERS : SLIDERS.slice(0, 1)).map((pair, index) => (
              <FadeUp key={pair.title} delay={index === 0 ? 0.1 : 0} className="space-y-3" data-testid={`slider-slot-${index}`}>
                <h3 className="font-heading font-bold text-lg uppercase tracking-wider text-neutral-200">{pair.title}</h3>
                {pair.before && pair.after
                  ? <BeforeAfterSlider pair={pair} />
                  : <PlaceholderSlide title={pair.title} testid={`slider-placeholder-${index}`} />}
              </FadeUp>
            ))}
          </div>

          {!showAllTransforms && (
            <FadeUp className="text-center">
              <button
                onClick={() => setShowAllTransforms(true)}
                className="border border-yellow-500/50 text-yellow-400 font-heading font-bold tracking-wider px-10 py-4 uppercase text-sm hover:bg-yellow-500 hover:text-black transition-colors"
                data-testid="view-more-transformations"
              >
                View More Transformations
              </button>
            </FadeUp>
          )}
        </div>
      </section>

      {/* REAL WORK GALLERY */}
      <section id="work" className="py-20 px-6 bg-[#0A0A0A] border-t border-neutral-900" data-testid="work-gallery-section">
        <div className="max-w-6xl mx-auto space-y-10">
          <FadeUp className="space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">Real JackWash Work</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight uppercase">OUR <span className="gold-gradient-text">WORK</span></h2>
          </FadeUp>
          <FadeUp delay={0.1} className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {GALLERY_IMAGES.map((image, index) => (
              <figure key={image.src} className="group relative overflow-hidden border border-neutral-800 bg-neutral-900">
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                  data-testid={`gallery-image-${index}`}
                />
                <figcaption className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur px-4 py-2 text-xs font-semibold uppercase tracking-wider text-yellow-400">
                  {image.label}
                </figcaption>
              </figure>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* REVIEWS — real Google reviews only */}
      <section id="reviews" className="py-20 px-6 bg-[#0D0D0D] border-t border-neutral-900" data-testid="reviews-section">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <FadeUp className="space-y-3">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-red-500">Verified Feedback</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight uppercase">
              <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity" data-testid="reviews-title-link">
                REAL GOOGLE <span className="gold-gradient-text">REVIEWS</span>
              </a>
            </h2>
            <p className="text-neutral-400 text-base max-w-xl mx-auto">
              Every JackWash review lives publicly on Google — real customers, unfiltered. Read them yourself before you book.
            </p>
          </FadeUp>

          {GOOGLE_REVIEWS.length > 0 && (
            <div className="grid gap-6 text-left" data-testid="google-review-cards">
              {GOOGLE_REVIEWS.map((review) => (
                <FadeUp key={review.name} className="border border-neutral-800 bg-[#111111] p-6 sm:p-8 space-y-4">
                  <div className="flex items-center gap-1 text-yellow-500">
                    {[...Array(review.stars)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 fill-yellow-500" viewBox="0 0 24 24"><path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7-6.2-3.7-6.2 3.7 1.6-7L2 9.2l7.1-.6z" /></svg>
                    ))}
                    <GoogleIcon className="w-4 h-4 ml-2 text-neutral-400" />
                  </div>
                  <p className="text-neutral-200 text-base leading-relaxed">&ldquo;{review.text}&rdquo;</p>
                  <div className="flex items-center justify-between pt-3 border-t border-neutral-800">
                    <span className="font-heading font-bold uppercase tracking-wider text-sm">{review.name}</span>
                    {review.vehicle && <span className="text-xs text-yellow-500 uppercase tracking-wider">{review.vehicle}</span>}
                  </div>
                </FadeUp>
              ))}
            </div>
          )}
          <FadeUp delay={0.15} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-white text-neutral-900 font-heading font-bold tracking-wider px-8 py-4 uppercase text-sm hover:bg-yellow-400 transition-colors"
              data-testid="google-reviews-link"
            >
              <GoogleIcon className="w-5 h-5" /> Read More Reviews on Google <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs uppercase tracking-wider text-neutral-400 hover:text-yellow-400 underline underline-offset-4 transition-colors"
              data-testid="google-leave-review-link"
            >
              Had a detail with us? Leave a review
            </a>
          </FadeUp>
        </div>
      </section>

      {/* ABOUT & CONTACT FOOTER */}
      <footer id="contact" className="bg-[#050505] border-t border-neutral-900 py-16 px-6" data-testid="footer">
        <div className="max-w-4xl mx-auto space-y-10 text-center">
          <FadeUp className="space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <img src={LOGO_URL} alt="JackWash logo" className="h-10 w-10 rounded-full border border-red-500/50 object-cover" data-testid="footer-logo-image" />
              <span className="font-heading font-black text-2xl tracking-wider">JACK<span className="text-red-600">WASH</span></span>
            </div>
            <h3 className="font-heading font-bold text-xl uppercase tracking-wider text-yellow-500">About JackWash</h3>
            <p className="text-neutral-400 text-sm leading-relaxed max-w-xl mx-auto">
              JackWash is an owner-operated premium mobile detailing service on the Gold Coast. Every vehicle is detailed personally by Jack — from family SUVs to AMGs — to one standard: uncompromising.
            </p>
            <p className="text-xs text-neutral-500 max-w-xl mx-auto" data-testid="water-power-note">{WATER_NOTE}</p>
          </FadeUp>

          <FadeUp delay={0.1} className="flex flex-wrap items-center justify-center gap-4">
            <a href={PHONE_TEL} className="flex items-center gap-2 border border-neutral-700 px-5 py-3 text-sm font-semibold text-white hover:border-yellow-500/60 hover:text-yellow-400 transition-colors" data-testid="footer-phone-link">
              <Phone className="w-4 h-4 text-yellow-500" /> {PHONE_DISPLAY}
            </a>
            <a href={`mailto:${EMAIL}`} className="flex items-center gap-2 border border-neutral-700 px-5 py-3 text-sm font-semibold text-white hover:border-yellow-500/60 hover:text-yellow-400 transition-colors" data-testid="footer-email-link">
              {EMAIL}
            </a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border border-neutral-700 px-5 py-3 text-sm font-semibold text-white hover:border-yellow-500/60 hover:text-yellow-400 transition-colors" data-testid="footer-instagram-link">
              <InstagramIcon className="w-4 h-4 text-yellow-500" /> @jackwash.aus
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border border-neutral-700 px-5 py-3 text-sm font-semibold text-white hover:border-yellow-500/60 hover:text-yellow-400 transition-colors" data-testid="footer-whatsapp-link">
              <WhatsAppIcon className="w-4 h-4 text-yellow-500" /> WhatsApp
            </a>
          </FadeUp>

          <div className="pt-8 border-t border-neutral-900 text-xs text-neutral-500 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p>© 2026 JackWash Mobile Car Detailing, Gold Coast. All rights reserved.</p>
            <button onClick={openQuote} className="text-red-500 font-semibold hover:underline uppercase tracking-wider" data-testid="footer-quote-link">Get Your Quote</button>
          </div>
        </div>
      </footer>

      <JackWashQuoteModal isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} />
    </div>
  );
}
